import { useState, useEffect } from 'react';
import { Firestore, collection, query, orderBy, onSnapshot, doc } from 'firebase/firestore';

interface PatternRule {
  id: string;
  condition: {
    column?: string;
    operator?: string;
    value?: string;
    always?: boolean;
  };
  pattern_config: {
    pattern_project_id: string;
    pattern_thread_id: string;
    from_picture_id: string;
    to_picture_id: string;
    steps?: Array<{
      picture_id: string;
      seq: number;
      op: string;
      meta: Record<string, any>;
    }>;
    prompt_overrides?: Record<string, Array<{ find: string; replace: string }>>;
    additionnal_pictures?: Array<{
      picture_id: string;
      category: string;
    }>;
    additionnal_picture_overrides?: Array<{
      source: 'column' | 'static';
      column?: string;
      url?: string;
    }>;
  };
}

interface BulkTreatment {
  treatment_id: string;
  name: string;
  created_at: Date;
  updated_at: Date;
  created_by: string;
  status: 'draft' | 'processing' | 'completed' | 'failed' | 'cancelled';
  treatment_mode: 'prompt' | 'pattern';
  bigquery_config: {
    project_id: string;
    dataset_id: string;
    table_name: string;
    selected_columns: string[];
    image_column: string;
  };
  prompt_rules: Record<string, any>;
  pattern_rules?: PatternRule[];
  prompt_variables?: string[];
  media_type: 'image' | 'video';
  total_rows: number;
  processed_rows: number;
  error_message?: string;
  results_table?: string;
  remove_background?: boolean;
}

export function useBulkTreatments(
  db: Firestore,
  projectId: string | undefined,
  uid: string | undefined
) {
  const [treatments, setTreatments] = useState<BulkTreatment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId || !uid) {
      setTreatments([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const q = query(
      collection(db, 'projects', projectId, 'bulk_treatments'),
      orderBy('created_at', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const treatmentList: BulkTreatment[] = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            treatment_id: doc.id,
            name: data.name || 'Untitled Treatment',
            created_at: data.created_at?.toDate() || new Date(),
            updated_at: data.updated_at?.toDate() || new Date(),
            created_by: data.created_by || '',
            status: data.status || 'draft',
            treatment_mode: data.treatment_mode || 'prompt',
            media_type: data.media_type || 'image',
            bigquery_config: data.bigquery_config || {},
            prompt_rules: data.prompt_rules || '',
            pattern_rules: data.pattern_rules,
            prompt_variables: data.prompt_variables,
            total_rows: data.total_rows || 0,
            processed_rows: data.processed_rows || 0,
            error_message: data.error_message,
            results_table: data.results_table,
            remove_background: data.remove_background || false,
          };
        });

        setTreatments(treatmentList);
        setLoading(false);
      },
      (err) => {
        setError(err.message || 'Failed to load treatments');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [db, projectId, uid]);

  return { treatments, loading, error };
}

export function useBulkTreatment(
  db: Firestore,
  projectId: string | undefined,
  treatmentId: string | undefined
) {
  const [treatment, setTreatment] = useState<BulkTreatment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId || !treatmentId) {
      setTreatment(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const docRef = doc(db, 'projects', projectId, 'bulk_treatments', treatmentId);

    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          setTreatment({
            treatment_id: docSnapshot.id,
            name: data.name || 'Untitled Treatment',
            created_at: data.created_at?.toDate() || new Date(),
            updated_at: data.updated_at?.toDate() || new Date(),
            created_by: data.created_by || '',
            status: data.status || 'draft',
            treatment_mode: data.treatment_mode || 'prompt',
            media_type: data.media_type || 'image',
            bigquery_config: data.bigquery_config || {},
            prompt_rules: data.prompt_rules || '',
            pattern_rules: data.pattern_rules,
            prompt_variables: data.prompt_variables,
            total_rows: data.total_rows || 0,
            processed_rows: data.processed_rows || 0,
            error_message: data.error_message,
            results_table: data.results_table,
            remove_background: data.remove_background || false,
          });
        } else {
          setTreatment(null);
        }
        setLoading(false);
      },
      (err) => {
        setError(err.message || 'Failed to load treatment');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [db, projectId, treatmentId]);

  return { treatment, loading, error };
}
